import type { CSSProperties } from 'react';
import { Button, Flex, Text } from '@nula/core-ui';
import { failureCopy } from '@/lib/templates/failure-copy';
import type { TemplateSection } from '@/lib/templates/catalog';
import { useTemplate } from '@/lib/templates/use-templates-store';
import type { SurfaceEntry } from '@/lib/sync/document-surfaces';
import styles from './surface-failed-body.module.css';

const FIRST_SECTION_LINE_WIDTHS = ['88%', '70%'];
const LATER_SECTION_LINE_WIDTHS = ['82%', '64%'];
const GENERIC_LINE_WIDTHS = ['88%', '82%', '64%'];

function OutlineLines({ widths }: { widths: string[] }) {
  return widths.map((width, i) => (
    <Flex
      key={i}
      data-testid="outline-line"
      className={styles.line}
      style={{ '--line-width': width } as CSSProperties}
    />
  ));
}

function outlineLineWidths(sections: TemplateSection[]): string[][] {
  let later = 0;
  return sections.map((section, i) => {
    if (!section.required) return [];
    if (i === 0) return FIRST_SECTION_LINE_WIDTHS;
    return [LATER_SECTION_LINE_WIDTHS[later++ % LATER_SECTION_LINE_WIDTHS.length]];
  });
}

interface SurfaceFailedBodyProps {
  entry: SurfaceEntry;
  sourceTitles: readonly string[];
  canEdit: boolean;
  viewerIsRequester: boolean;
  /** Absent when there is no known template to try again with. */
  onTryAgain?: () => void;
  onRemove: () => void;
}

export function SurfaceFailedBody({
  entry,
  sourceTitles,
  canEdit,
  viewerIsRequester,
  onTryAgain,
  onRemove,
}: SurfaceFailedBodyProps) {
  const template = useTemplate(entry.generator);
  const outputType = entry.surface === 'canvas' ? 'canvas' : 'text';
  const copy = failureCopy({
    failure: entry.failure ?? null,
    outputType,
    templateName: template?.name ?? null,
    sourceTitles,
    viewerIsRequester,
    canEdit,
  });
  const sections = template?.sections ?? [];
  const lineWidths = outlineLineWidths(sections);

  return (
    <Flex vertical className={styles.failedRoot}>
      <Flex vertical role="alert" className={styles.message}>
        <Text size="sm">{copy.headline}</Text>
        <Text type="secondary" size="sm">{copy.safety}</Text>
        {copy.reason && <Text type="secondary" size="sm">{copy.reason}</Text>}
      </Flex>
      {canEdit && (
        <Flex className={styles.actions}>
          {onTryAgain && copy.retry !== 'absent' && (
            <Button size="sm" variant={copy.retry === 'primary' ? 'primary' : 'default'} onClick={onTryAgain}>
              Try again
            </Button>
          )}
          <Button size="sm" variant="default" onClick={onRemove}>
            Remove
          </Button>
        </Flex>
      )}
      <Flex vertical aria-hidden="true" data-testid="failed-outline" className={styles.outline}>
        {outputType === 'canvas' ? (
          <Flex data-testid="failed-outline-canvas" className={styles.frame} />
        ) : sections.length > 0 ? (
          sections.map((section, i) => (
            <Flex vertical key={section.key} data-testid="outline-section" className={styles.outlineSection}>
              <Flex align="baseline">
                <Text size="lg" strong className={styles.outlineHeading}>{section.heading}</Text>
                {!section.required && (
                  <Text size="xs" className={styles.ifAny}>if any</Text>
                )}
              </Flex>
              <OutlineLines widths={lineWidths[i]} />
            </Flex>
          ))
        ) : (
          <OutlineLines widths={GENERIC_LINE_WIDTHS} />
        )}
      </Flex>
    </Flex>
  );
}
